import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Grid, FormControl, InputLabel, Select, MenuItem, Button,
    Table, TableBody, TableCell, TableContainer, TableRow, Paper, 
    TableHead, TextField, Checkbox, Chip, Card, CardContent,
    Alert, Modal, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { format, parseISO } from 'date-fns';
import { useUser } from '../contexts/UserContext';
import Swal from 'sweetalert2';
import { useAccesos } from '../contexts/AccesosContext';

function ConciliacionBancaria() {
    const server = process.env.REACT_APP_API_ROUTE;
    const { user } = useUser();
    const { tienePermiso } = useAccesos();

    // Estados para filtros
    const [bancos, setBancos] = useState([]);
    const [cuentasBancarias, setCuentasBancarias] = useState([]);
    const [idBanco, setIdBanco] = useState('');
    const [idCuentaBancaria, setIdCuentaBancaria] = useState('');
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    
    // Estados para movimientos y conciliación
    const [movimientos, setMovimientos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [descripcion, setDescripcion] = useState('');
    
    // Estados para lista de conciliaciones guardadas
    const [conciliaciones, setConciliaciones] = useState([]);
    const [loadingConciliaciones, setLoadingConciliaciones] = useState(false);
    const [imprimiendoId, setImprimiendoId] = useState(null);
    const [busquedaConciliacion, setBusquedaConciliacion] = useState('');
    
    // Estados para modal de visualización PDF
    const [openModalPdf, setOpenModalPdf] = useState(false);
    const [pdfUrl, setPdfUrl] = useState('');
    const [pdfNombre, setPdfNombre] = useState('');

    // Cargar catálogo de bancos y conciliaciones al montar
    useEffect(() => {
        cargarBancos();
        cargarConciliaciones();
    }, []);

    // Limpiar URL del PDF al cerrar modal
    useEffect(() => {
        if (!openModalPdf && pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl('');
        }
    }, [openModalPdf]);

    // Cargar cuentas bancarias cuando cambia el banco
    useEffect(() => {
        if (idBanco) {
            cargarCuentasBancarias(idBanco);
        } else {
            setCuentasBancarias([]);
            setIdCuentaBancaria('');
        }
    }, [idBanco]);

    const cargarBancos = async () => {
        try {
            const response = await fetch(`${server}/ChkBancoes/catalogo`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setBancos(data.$values || data);
            }
        } catch (error) {
            console.error('Error al cargar bancos:', error);
            Swal.fire('Error', 'No se pudieron cargar los bancos', 'error');
        }
    };

    const cargarCuentasBancarias = async (bancoId) => {
        try {
            const response = await fetch(`${server}/ChkBancosCuentasBancarias/banco/${bancoId}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setCuentasBancarias(data.$values || data);
            }
        } catch (error) {
            console.error('Error al cargar cuentas bancarias:', error);
            Swal.fire('Error', 'No se pudieron cargar las cuentas bancarias', 'error');
        }
    };

    const cargarConciliaciones = async () => {
        setLoadingConciliaciones(true);
        try {
            const response = await fetch(`${server}/ctb/concilacion-bancaria/all`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setConciliaciones(data.$values || data);
            }
        } catch (error) {
            console.error('Error al cargar conciliaciones:', error);
            Swal.fire('Error', 'No se pudieron cargar las conciliaciones guardadas', 'error');
        } finally {
            setLoadingConciliaciones(false);
        }
    };

    const imprimirConciliacion = async (id) => {
        setImprimiendoId(id);
        try {
            const response = await fetch(`${server}/InformesContablesLinux/conciliacion/${id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Accept': 'application/pdf'
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const urlBlob = window.URL.createObjectURL(blob);
                
                // Configurar y abrir modal con el PDF
                const nombreArchivo = `Conciliacion_${id}_${new Date().toISOString().split('T')[0]}.pdf`;
                setPdfUrl(urlBlob);
                setPdfNombre(nombreArchivo);
                setOpenModalPdf(true);
            } else {
                const errorData = await response.json().catch(() => ({}));
                Swal.fire('Error', errorData.message || 'No se pudo generar el reporte', 'error');
            }
        } catch (error) {
            console.error('Error al imprimir:', error);
            Swal.fire('Error', 'Error al generar el reporte de conciliación', 'error');
        } finally {
            setImprimiendoId(null);
        }
    };

    // Cerrar modal PDF
    const handleCloseModalPdf = () => {
        setOpenModalPdf(false);
    };

    // Descargar PDF
    const handleDownloadPdf = () => {
        if (pdfUrl && pdfNombre) {
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.download = pdfNombre;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            Swal.fire({
                icon: 'success',
                title: 'Descargado',
                text: 'El PDF se ha descargado correctamente',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };

    const buscarMovimientos = async () => {
        if (!idBanco || !idCuentaBancaria || !fechaInicio || !fechaFin) {
            Swal.fire('Validación', 'Todos los filtros son obligatorios', 'warning');
            return;
        }

        if (fechaInicio > fechaFin) {
            Swal.fire('Validación', 'La fecha de inicio no puede ser mayor a la fecha fin', 'warning');
            return;
        }

        setLoading(true);
        try {
            // Convertir fechas de yyyy-MM-dd a yyyyMMdd (formato requerido por el backend)
            const fechaInicioFormateada = fechaInicio.replace(/-/g, '');
            const fechaFinFormateada = fechaFin.replace(/-/g, '');
            
            const response = await fetch(
                `${server}/ctb/concilacion-bancaria/movimientos/${idCuentaBancaria}/${idBanco}/${fechaInicioFormateada}/${fechaFinFormateada}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                const movimientosCrudos = data.$values || data;
                
                const movimientosConEstado = movimientosCrudos.map(mov => ({
                    // Mapeo explícito para asegurar compatibilidad PascalCase/camelCase
                    id: mov.id || mov.Id,
                    idConciliacionBancaria: mov.idConciliacionBancaria || mov.IdConciliacionBancaria || 0,
                    idAsientoContable: mov.idAsientoContable || mov.IdAsientoContable || 0,
                    fechaBancaria: mov.fechaBancaria || mov.FechaBancaria || '',
                    comentario: mov.comentario || mov.Comentario || '',
                    isConciliado: mov.isConciliado || mov.IsConciliado || false,
                    // Mantener todos los demás campos originales
                    ...mov,
                    // Estados del frontend
                    seleccionado: false
                }));
                
                setMovimientos(movimientosConEstado);
                
                // Generar descripción automática
                const bancoSeleccionado = bancos.find(b => b.idBanco === parseInt(idBanco));
                const cuentaSeleccionada = cuentasBancarias.find(c => c.idCuentaBancaria === parseInt(idCuentaBancaria));
                const [yearIni, monthIni, dayIni] = fechaInicio.split('-');
                const [yearFin, monthFin, dayFin] = fechaFin.split('-');
                const descGen = `Conciliación ${bancoSeleccionado?.nombreBanco || ''} - ${cuentaSeleccionada?.cuentaBancaria || ''} - ${dayIni}/${monthIni}/${yearIni} - ${dayFin}/${monthFin}/${yearFin}`;
                setDescripcion(descGen);
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.error('Error del servidor:', errorData);
                Swal.fire('Error', errorData.message || 'No se pudieron cargar los movimientos', 'error');
            }
        } catch (error) {
            console.error('Error al buscar movimientos:', error);
            Swal.fire('Error', 'Error al buscar movimientos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSeleccionarMovimiento = (index) => {
        const nuevosMovimientos = [...movimientos];
        const movimiento = nuevosMovimientos[index];
        
        // No permitir seleccionar si ya está conciliado
        if (movimiento.isConciliado) {
            return;
        }

        movimiento.seleccionado = !movimiento.seleccionado;
        setMovimientos(nuevosMovimientos);
    };

    const handleFechaBancariaChange = (index, fecha) => {
        const nuevosMovimientos = [...movimientos];
        nuevosMovimientos[index].fechaBancaria = fecha;
        setMovimientos(nuevosMovimientos);
    };

    const handleComentarioChange = (index, comentario) => {
        const nuevosMovimientos = [...movimientos];
        nuevosMovimientos[index].comentario = comentario;
        setMovimientos(nuevosMovimientos);
    };

    // Cálculos según especificaciones del backend
    // Total Valor Contable: Suma del Debe - Suma Haber de TODA la lista
    const calcularSaldoContable = () => {
        const totalDebe = movimientos.reduce((sum, mov) => sum + (mov.ctbAsientosContable?.debe || 0), 0);
        const totalHaber = movimientos.reduce((sum, mov) => sum + (mov.ctbAsientosContable?.haber || 0), 0);
        return totalDebe - totalHaber;
    };

    // Total Valor Bancario: Suma del debe - suma haber de lo CONCILIADO (seleccionado)
    const calcularSaldoBancario = () => {
        const movimientosSeleccionados = movimientos.filter(mov => mov.seleccionado);
        const totalDebe = movimientosSeleccionados.reduce((sum, mov) => sum + (mov.ctbAsientosContable?.debe || 0), 0);
        const totalHaber = movimientosSeleccionados.reduce((sum, mov) => sum + (mov.ctbAsientosContable?.haber || 0), 0);
        return totalDebe - totalHaber;
    };

    // Diferencia: Total Contable - Total Bancario
    const calcularDiferencia = () => {
        const saldoCont = calcularSaldoContable();
        const saldoBanc = calcularSaldoBancario();
        return saldoCont - saldoBanc;
    };

    const validarConciliacion = () => {
        const movimientosSeleccionados = movimientos.filter(mov => mov.seleccionado);
        
        if (movimientosSeleccionados.length === 0) {
            Swal.fire('Validación', 'Debe seleccionar al menos un movimiento', 'warning');
            return false;
        }

        // Validar que todos los movimientos seleccionados tengan fecha bancaria
        const sinFecha = movimientosSeleccionados.filter(mov => !mov.fechaBancaria);
        if (sinFecha.length > 0) {
            Swal.fire('Validación', 'Todos los movimientos seleccionados deben tener una fecha de estado de cuenta', 'warning');
            return false;
        }

        // Advertir si la diferencia es mayor al 5%
        const diferencia = Math.abs(calcularDiferencia());
        const saldoCont = Math.abs(calcularSaldoContable());
        if (saldoCont > 0 && (diferencia / saldoCont) > 0.05) {
            return Swal.fire({
                title: 'Diferencia Significativa',
                text: `La diferencia es mayor al 5% del saldo contable. ¿Desea continuar?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, continuar',
                cancelButtonText: 'Cancelar'
            });
        }

        return Promise.resolve({ isConfirmed: true });
    };

    const guardarConciliacion = async () => {
        const validacion = await validarConciliacion();
        if (!validacion || !validacion.isConfirmed) {
            return;
        }

        const movimientosSeleccionados = movimientos.filter(mov => mov.seleccionado);
        setLoading(true);

        try {
            const saldoCont = calcularSaldoContable();
            const saldoBanc = calcularSaldoBancario();
            const dif = calcularDiferencia();

            // Preparar el modelo de conciliación completo
            const usuarioPadded = (user.codigoUsuario || '').padEnd(20, ' ');
            
            const conciliacion = {
                idBanco: parseInt(idBanco),
                idCuentaBancaria: parseInt(idCuentaBancaria),
                fechaInicio: fechaInicio,
                fechaFin: fechaFin,
                descripcion: descripcion,
                saldoContable: saldoCont,
                saldoBancario: saldoBanc,
                diferencia: dif,
                codigoUsuario: user.codigoUsuario,
                ingresadoPor: usuarioPadded,
                modificadoPor: usuarioPadded,
                ctbConcilacionBancariaDetalles: movimientosSeleccionados.map(mov => ({
                    idAsientoContable: mov.idAsientoContable,
                    fechaBancaria: mov.fechaBancaria,
                    comentario: mov.comentario || null,
                    isConciliado: true
                }))
            };

            const response = await fetch(`${server}/ctb/concilacion-bancaria/conciliar`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(conciliacion)
            });

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Conciliación Guardada',
                    text: `Se conciliaron ${movimientosSeleccionados.length} movimientos`,
                    timer: 2000
                });
                
                // Refrescar las tablas
                buscarMovimientos();
                cargarConciliaciones();
            } else {
                const errorData = await response.json().catch(() => ({}));
                Swal.fire('Error', errorData.message || 'Hubo un error al guardar la conciliación', 'error');
            }
        } catch (error) {
            console.error('Error al guardar conciliación:', error);
            Swal.fire('Error', 'Error al guardar la conciliación', 'error');
        } finally {
            setLoading(false);
        }
    };

    const saldoContable = calcularSaldoContable();
    const saldoBancarioCalculado = calcularSaldoBancario();
    const diferencia = calcularDiferencia();
    const movimientosSeleccionados = movimientos.filter(mov => mov.seleccionado).length;

    return (
        <div className="right_col" role="main">
            <div className="page-title">
                <div className="title_left">
                    <h3>Conciliación Bancaria</h3>
                </div>
            </div>

            <div className="clearfix"></div>

            <div className="row">
                <div className="col-md-12 col-sm-12">
                    <div className="x_panel">
                        <div className="x_title">
                            <h2>Filtros de Búsqueda</h2>
                            <div className="clearfix"></div>
                        </div>
                        <div className="x_content">
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Banco *</InputLabel>
                                        <Select
                                            value={idBanco}
                                            onChange={(e) => setIdBanco(e.target.value)}
                                            label="Banco *"
                                        >
                                            <MenuItem value="">Seleccionar</MenuItem>
                                            {bancos.map((banco) => (
                                                <MenuItem key={banco.idBanco} value={banco.idBanco}>
                                                    {banco.nombreBanco}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Cuenta Bancaria *</InputLabel>
                                        <Select
                                            value={idCuentaBancaria}
                                            onChange={(e) => setIdCuentaBancaria(e.target.value)}
                                            label="Cuenta Bancaria *"
                                            disabled={!idBanco}
                                        >
                                            <MenuItem value="">Seleccionar</MenuItem>
                                            {cuentasBancarias.map((cuenta) => (
                                                <MenuItem key={cuenta.idCuentaBancaria} value={cuenta.idCuentaBancaria}>
                                                    {cuenta.descripcionCompleta}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField
                                        label="Fecha Inicio *"
                                        type="date"
                                        size="small"
                                        fullWidth
                                        value={fechaInicio}
                                        onChange={(e) => setFechaInicio(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField
                                        label="Fecha Fin *"
                                        type="date"
                                        size="small"
                                        fullWidth
                                        value={fechaFin}
                                        onChange={(e) => setFechaFin(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={2}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        onClick={buscarMovimientos}
                                        disabled={loading || !idBanco || !idCuentaBancaria || !fechaInicio || !fechaFin}
                                    >
                                        {loading ? 'Buscando...' : 'Buscar'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </div>
                    </div>
                </div>
            </div>

            {movimientos.length > 0 && (
                <>
                    <div className="row">
                        <div className="col-md-12">
                            <div className="x_panel">
                                <div className="x_content">
                                    <TextField
                                        label="Descripción de Conciliación"
                                        fullWidth
                                        value={descripcion}
                                        onChange={(e) => setDescripcion(e.target.value)}
                                        size="small"
                                        helperText="Edite la descripción si es necesario"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">
                            <Alert severity="info" sx={{ mb: 2 }}>
                                ℹ️ Es normal que no se llegue a conciliar al 100% en este momento. Las diferencias pueden deberse a movimientos en tránsito, cheques no cobrados o débitos pendientes.
                            </Alert>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-9 col-sm-12">
                            <div className="x_panel">
                                <div className="x_title">
                                    <h2>Movimientos Contables</h2>
                                    <div className="clearfix"></div>
                                </div>
                                <div className="x_content">
                                    <TableContainer component={Paper} style={{ maxHeight: 500 }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell padding="checkbox" style={{ width: 50 }}>Sel.</TableCell>
                                                    <TableCell>Fecha</TableCell>
                                                    <TableCell>Referencia</TableCell>
                                                    <TableCell>Descripción</TableCell>
                                                    <TableCell align="right">Debe</TableCell>
                                                    <TableCell align="right">Haber</TableCell>
                                                    <TableCell style={{ width: 150 }}>Fecha Estado Cuenta</TableCell>
                                                    <TableCell style={{ width: 150 }}>Comentario</TableCell>
                                                    <TableCell>Estado</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {movimientos.map((mov, index) => (
                                                    <TableRow 
                                                        key={index}
                                                        style={{ 
                                                            backgroundColor: mov.isConciliado ? '#f5f5f5' : mov.seleccionado ? '#e3f2fd' : 'white'
                                                        }}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={mov.seleccionado}
                                                                onChange={() => handleSeleccionarMovimiento(index)}
                                                                disabled={mov.isConciliado}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {mov.ctbAsientosContable?.idPartidaContableNavigation?.fechaPartida ? 
                                                                format(new Date(mov.ctbAsientosContable.idPartidaContableNavigation.fechaPartida), 'dd/MM/yyyy') : ''}
                                                        </TableCell>
                                                        <TableCell>{mov.ctbAsientosContable?.idPartidaContableNavigation?.secuencialManual || ''}</TableCell>
                                                        <TableCell>{mov.ctbAsientosContable?.concepto || ''}</TableCell>
                                                        <TableCell align="right">{mov.ctbAsientosContable?.debe?.toFixed(2) || '0.00'}</TableCell>
                                                        <TableCell align="right">{mov.ctbAsientosContable?.haber?.toFixed(2) || '0.00'}</TableCell>
                                                        <TableCell>
                                                            {!mov.isConciliado && (
                                                                <TextField
                                                                    type="date"
                                                                    size="small"
                                                                    fullWidth
                                                                    value={mov.fechaBancaria || ''}
                                                                    onChange={(e) => handleFechaBancariaChange(index, e.target.value)}
                                                                    disabled={!mov.seleccionado}
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                            )}
                                                            {mov.isConciliado && mov.fechaBancaria && (
                                                                <Typography variant="body2">
                                                                    {format(new Date(mov.fechaBancaria), 'dd/MM/yyyy')}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                fullWidth
                                                                value={mov.comentario}
                                                                onChange={(e) => handleComentarioChange(index, e.target.value)}
                                                                disabled={mov.isConciliado || !mov.seleccionado}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {mov.isConciliado ? (
                                                                <Chip label="Conciliado" size="small" color="success" />
                                                            ) : (
                                                                <Chip label="Pendiente" size="small" color="default" />
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-3 col-sm-12">
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Resumen de Conciliación
                                    </Typography>
                                    
                                    <Box sx={{ mt: 2 }}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12}>
                                                <Box sx={{ 
                                                    p: 1, 
                                                    bgcolor: '#f5f5f5', 
                                                    borderRadius: 1
                                                }}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Total Valor Contable:
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        ${saldoContable.toFixed(2)}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Box sx={{ 
                                                    p: 1, 
                                                    bgcolor: '#e3f2fd', 
                                                    borderRadius: 1
                                                }}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Total Valor Bancaria (Conciliado):
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        ${saldoBancarioCalculado.toFixed(2)}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Box sx={{ 
                                                    p: 1, 
                                                    bgcolor: diferencia === 0 ? '#e8f5e9' : '#fff3e0', 
                                                    borderRadius: 1
                                                }}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Dif.:
                                                    </Typography>
                                                    <Typography 
                                                        variant="h6"
                                                        color={diferencia === 0 ? 'success.main' : 'warning.main'}
                                                    >
                                                        ${diferencia.toFixed(2)}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Box sx={{ 
                                                    p: 1, 
                                                    bgcolor: '#e3f2fd', 
                                                    borderRadius: 1,
                                                    mt: 1
                                                }}>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Movimientos Seleccionados:
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        {movimientosSeleccionados}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} sx={{ mt: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    fullWidth
                                                    onClick={guardarConciliacion}
                                                    disabled={loading || movimientosSeleccionados === 0 || !tienePermiso('Conciliar Transacción')}
                                                >
                                                    Guardar Conciliación
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </>
            )}

            {movimientos.length === 0 && !loading && (
                <div className="row">
                    <div className="col-md-12">
                        <Alert severity="info">
                            Seleccione los filtros y haga clic en "Buscar" para cargar los movimientos contables.
                        </Alert>
                    </div>
                </div>
            )}

            {/* Lista de Conciliaciones Guardadas */}
            <div className="row" style={{ marginTop: 40 }}>
                <div className="col-md-12">
                    <div className="x_panel">
                        <div className="x_title">
                            <h2>Conciliaciones Bancarias Guardadas</h2>
                            <div className="clearfix"></div>
                        </div>
                        <div className="x_content">
                            {/* Campo de búsqueda */}
                            <Grid container spacing={2} style={{ marginBottom: 20 }}>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Buscar conciliaciones"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={busquedaConciliacion}
                                        onChange={(e) => setBusquedaConciliacion(e.target.value)}
                                        placeholder="Buscar por código, banco, cuenta, descripción..."
                                    />
                                </Grid>
                            </Grid>

                            {loadingConciliaciones ? (
                                <Box sx={{ textAlign: 'center', py: 3 }}>
                                    <Typography>Cargando conciliaciones...</Typography>
                                </Box>
                            ) : (
                                <TableContainer component={Paper}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ width: 80 }}>ID</TableCell>
                                                <TableCell style={{ width: 120 }}>Código</TableCell>
                                                <TableCell style={{ width: 200 }}>Banco</TableCell>
                                                <TableCell style={{ width: 150 }}>Cuenta Bancaria</TableCell>
                                                <TableCell style={{ width: 250 }}>Descripción</TableCell>
                                                <TableCell style={{ width: 120 }}>Fecha Emisión</TableCell>
                                                <TableCell align="right" style={{ width: 130 }}>Saldo Contable</TableCell>
                                                <TableCell align="right" style={{ width: 130 }}>Saldo Bancario</TableCell>
                                                <TableCell align="right" style={{ width: 130 }}>Diferencia</TableCell>
                                                <TableCell align="center" style={{ width: 100 }}>Estado</TableCell>
                                                <TableCell align="center" style={{ width: 180 }}>Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(() => {
                                                // Filtrar conciliaciones según búsqueda
                                                const conciliacionesFiltradas = conciliaciones.filter((conc) => {
                                                    const query = busquedaConciliacion.toLowerCase();
                                                    return (
                                                        (conc.codigo && conc.codigo.toLowerCase().includes(query)) ||
                                                        (conc.chkBancosNavigation?.nombreBanco && conc.chkBancosNavigation.nombreBanco.toLowerCase().includes(query)) ||
                                                        (conc.idCuentaBancariaNavigation?.cuentaBancaria && conc.idCuentaBancariaNavigation.cuentaBancaria.toLowerCase().includes(query)) ||
                                                        (conc.descripcion && conc.descripcion.toLowerCase().includes(query))
                                                    );
                                                });

                                                return conciliacionesFiltradas.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={11} align="center">
                                                            <Typography variant="body2" color="textSecondary">
                                                                {busquedaConciliacion ? 'No se encontraron resultados para la búsqueda' : 'No hay conciliaciones guardadas'}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                conciliacionesFiltradas.map((conc) => (
                                                    <TableRow key={conc.id}>
                                                        <TableCell>{conc.id}</TableCell>
                                                        <TableCell>{conc.codigo || 'N/A'}</TableCell>
                                                        <TableCell>
                                                            {conc.chkBancosNavigation?.nombreBanco || 'N/A'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {conc.idCuentaBancariaNavigation?.cuentaBancaria || 'N/A'}
                                                        </TableCell>
                                                        <TableCell>{conc.descripcion}</TableCell>
                                                        <TableCell>
                                                            {conc.fechaIngreso ? format(new Date(conc.fechaIngreso), 'dd/MM/yyyy') : 'N/A'}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            ${conc.saldoContable?.toFixed(2) || '0.00'}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            ${conc.saldoBancario?.toFixed(2) || '0.00'}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <span style={{ 
                                                                color: conc.diferencia === 0 ? 'green' : 'red',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                ${conc.diferencia?.toFixed(2) || '0.00'}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Chip 
                                                                label={conc.diferencia === 0 ? 'Cuadrada' : 'Pendiente'}
                                                                size="small"
                                                                color={conc.diferencia === 0 ? 'success' : 'warning'}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Button
                                                                variant="contained"
                                                                color="primary"
                                                                size="small"
                                                                onClick={() => imprimirConciliacion(conc.id)}
                                                                disabled={imprimiendoId === conc.id}
                                                            >
                                                                {imprimiendoId === conc.id ? 'Generando...' : 'Ver Reporte'}
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            );
                                            })()}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal para visualizar PDF */}
            <Modal open={openModalPdf} onClose={handleCloseModalPdf}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        height: '90vh',
                        bgcolor: 'white',
                        boxShadow: 24,
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    {/* Header del modal */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2,
                            borderBottom: '1px solid #ddd',
                            bgcolor: '#f5f5f5'
                        }}
                    >
                        <Typography variant="h6" component="h2">
                            {pdfNombre}
                        </Typography>
                        <Box>
                            <IconButton
                                onClick={handleDownloadPdf}
                                color="primary"
                                title="Descargar PDF"
                                sx={{ mr: 1 }}
                            >
                                <DownloadIcon />
                            </IconButton>
                            <IconButton
                                onClick={handleCloseModalPdf}
                                color="error"
                                title="Cerrar"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Contenedor del PDF */}
                    <Box
                        sx={{
                            flex: 1,
                            overflow: 'hidden',
                            bgcolor: '#525659'
                        }}
                    >
                        {pdfUrl && (
                            <iframe
                                src={pdfUrl}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none'
                                }}
                                title="Visor de PDF"
                            />
                        )}
                    </Box>

                    {/* Footer del modal */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            p: 2,
                            borderTop: '1px solid #ddd',
                            bgcolor: '#f5f5f5'
                        }}
                    >
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleCloseModalPdf}
                            startIcon={<CloseIcon />}
                        >
                            CERRAR
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </div>
    );
}

export default ConciliacionBancaria;
